import React from 'react'

import styled, { keyframes } from 'styled-components'

import { useAIActivity } from '@/context/aiActivityContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
// Gradient animation keyframes
const gradientAnimation1 = keyframes`
  0%, 100% {
    stop-color: hsl(284, 100%, 60%);
  }
  50% {
    stop-color: hsl(33, 100%, 60%);
  }
`

const gradientAnimation2 = keyframes`
  0%, 100% {
    stop-color: hsl(203, 100%, 60%);
  }
  50% {
    stop-color: hsl(214, 100%, 60%);
  }
`

// Styled components
const GlowEffectWrapper = styled.div<{ blur: number }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  svg {
    filter: blur(${(props) => props.blur}px);
  }
`

const GradientBorder = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  stop:nth-child(1) {
    animation: ${gradientAnimation1} 5s infinite;
  }

  stop:nth-child(2) {
    animation: ${gradientAnimation2} 2.5s infinite reverse;
  }
`

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
  opacity: 0; // Start hidden
  transition: opacity 0.5s ease-in-out; // Add transition

  &.visible {
    opacity: 1; // Show when the class "visible" is added
  }
`

const GradientBorderComponent: React.FC<{ size: number; blur: number }> = ({
  size,
  blur,
}) => (
  <GlowEffectWrapper blur={blur}>
    <GradientBorder>
      <defs>
        <linearGradient id="gradient" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="hsl(284, 100%, 60%)" />
          <stop offset="100%" stopColor="hsl(203, 100%, 60%)" />
        </linearGradient>
      </defs>
      <rect width="100%" height={size} fill="url(#gradient)" y="0" />
      <rect
        width={size}
        height="100%"
        fill="url(#gradient)"
        x={`calc(100% - ${size}px)`}
      />
      <rect
        width="100%"
        height={size}
        fill="url(#gradient)"
        y={`calc(100% - ${size}px)`}
      />
      <rect width={size} height="100%" fill="url(#gradient)" x="0" />
    </GradientBorder>
  </GlowEffectWrapper>
)

interface AIGlowEffectProps {
  sizeFactor?: number
}

export const AIGlowEffect: React.FC<AIGlowEffectProps> = ({ sizeFactor }) => {
  const { isAIRunning } = useAIActivity()

  const isMobile = useBreakpoint('sm')
  const factor = sizeFactor ? sizeFactor : isMobile ? 0.75 : 1

  return isAIRunning ? (
    <Container className={isAIRunning ? 'visible' : ''}>
      <GradientBorderComponent size={20 * factor} blur={40 * factor} />
      <GradientBorderComponent size={8 * factor} blur={12 * factor} />
      <GradientBorderComponent size={4 * factor} blur={8 * factor} />
    </Container>
  ) : null
}
